import { useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  Building2,
  Calendar,
  Check,
  ChevronLeft,
  ExternalLink,
  Flag,
  Inbox,
  Loader2,
  Lock,
  RotateCcw,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/useAuthStore";
import { clientRoutes } from "@/lib/clientRoutes";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import {
  useEditTask,
  useEditTaskWatchers,
  usePostponeTask,
  useReopenTask,
  useTaskDetail,
} from "@/hooks/useTasks";
import { WatcherPicker } from "./WatcherPicker";
import type { TaskWatcher } from "@/api/types";
import {
  AttachmentUpload,
  type PendingAttachment,
} from "@/components/attachments/AttachmentUpload";
import { AttachmentList } from "@/components/attachments/AttachmentList";
import type { Attachment } from "@/api/types";
import { TaskCompleteModal } from "./TaskCompleteModal";
import { OUTCOME_LABEL, type TaskOutcome } from "@/api/client360Tasks";
import { PRIORITY_LABELS } from "./constants";

interface Props {
  open: boolean;
  onClose: () => void;
  taskId: string | null;
}

const STATUS_LABEL: Record<string, string> = {
  not_started: "Не розпочато",
  in_progress: "В роботі",
  completed: "Виконано",
  cancelled: "Скасовано",
  deferred: "Відкладено",
};

const SUBTYPE_LABEL: Record<string, string> = {
  generic: "Завдання",
  call: "Дзвінок",
  follow_up: "Follow-up",
  bi_signal: "BI-сигнал",
};

function shortDate(at: string | null): string {
  if (!at) return "—";
  try {
    const d = new Date(at.includes("T") ? at : at.replace(" ", "T") + "Z");
    return d.toLocaleDateString("uk-UA", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return at;
  }
}

export function TaskDetailDrawer({ open, onClose, taskId }: Props) {
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();

  const detailQ = useTaskDetail(open ? taskId : null);
  const reopenMut = useReopenTask();
  const postponeMut = usePostponeTask();
  const editMut = useEditTask();
  // D-142.W1 — мутація watchers (окремий хук: інвалідує і detail-ключ, П-9).
  const watchersMut = useEditTaskWatchers();
  const [watchersError, setWatchersError] = useState<string | null>(null);

  const [postponing, setPostponing] = useState(false);
  const [customDate, setCustomDate] = useState("");
  // TASK-ATT v1.3 (W2) — local attachment-edit state.
  // keptSavedIds = saved ids still bound (a removed saved id is dropped here →
  // it leaves the merged list = unlink on the next save). pendingAtt = uploaded-
  // but-not-yet-persisted queue (lifted from AttachmentUpload).
  const [keptSavedIds, setKeptSavedIds] = useState<string[] | null>(null);
  const [pendingAtt, setPendingAtt] = useState<PendingAttachment[]>([]);
  const [attError, setAttError] = useState<string | null>(null);
  // v1.2 R1/§20 — completion ALWAYS goes through the canonical outcome modal
  // (no one-click plain flip).
  const [completeOpen, setCompleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentUserId = useAuthStore((s) => s.user?.id);
  const userType = useAuthStore((s) => s.user?.type);

  const task = detailQ.data;
  const isCompleted = task?.status === "completed";
  // TASK-ATT v1.3 (W2/AC-TASK-LOCK): completed/cancelled → view/download ONLY.
  const attachmentsLocked =
    task?.status === "completed" || task?.status === "cancelled";
  const savedAttachments = useMemo<Attachment[]>(
    () => task?.attachments ?? [],
    [task?.attachments],
  );
  // FS-3 (§0/§18.2): Task ACL = edit:own (admin/super-admin = edit:all) → gate footer (UI-only).
  const isOwn =
    userType === "admin" ||
    userType === "super-admin" ||
    (!!task && !!currentUserId && task.assigneeId === currentUserId);

  const handleReopen = async () => {
    if (!task) return;
    setError(null);
    try {
      await reopenMut.mutateAsync(task.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не вдалося відновити");
    }
  };

  const handlePostpone = async (mode: "tomorrow" | "next_week" | "custom") => {
    if (!task) return;
    setError(null);
    try {
      await postponeMut.mutateAsync({
        id: task.id,
        body: { mode, newDate: mode === "custom" ? customDate : undefined },
      });
      setPostponing(false);
      setCustomDate("");
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Не вдалося перенести");
    }
  };

  // Initialise / re-sync the kept-saved set from the detail response. Resets
  // local edits when the drawer switches task or the detail reloads. Pending
  // uploads + removals are cleared so a re-open starts from the persisted truth.
  useEffect(() => {
    if (!task) return;
    setKeptSavedIds((task.attachments ?? []).map((a) => a.id));
    setPendingAtt([]);
    setAttError(null);
    // Re-sync only on task switch / persisted-count change, not every refetch
    // identity churn; reading task.attachments inside is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, task?.attachmentCount]);

  // Reset everything on close so the next open re-derives from fresh detail.
  useEffect(() => {
    if (!open) {
      setKeptSavedIds(null);
      setPendingAtt([]);
      setAttError(null);
    }
  }, [open]);

  // Saved rows still bound (a removed saved id drops out of keptSavedIds).
  const keptSaved = useMemo<Attachment[]>(() => {
    if (keptSavedIds === null) return savedAttachments;
    const keep = new Set(keptSavedIds);
    return savedAttachments.filter((a) => keep.has(a.id));
  }, [savedAttachments, keptSavedIds]);

  // Full merged list to submit = kept saved ids + pending ids (🔴 NEVER partial).
  const mergedIds = useMemo(
    () => [
      ...(keptSavedIds ?? savedAttachments.map((a) => a.id)),
      ...pendingAtt.map((p) => p.id),
    ],
    [keptSavedIds, savedAttachments, pendingAtt],
  );

  // Early-out AFTER all hooks (rules-of-hooks): unconditional hook order.
  if (!open) return null;

  // Dirty = a saved attachment removed OR a new file uploaded this session.
  const attachmentsDirty =
    pendingAtt.length > 0 ||
    (keptSavedIds !== null && keptSavedIds.length !== savedAttachments.length);

  const handleRemoveSaved = (id: string) => {
    setAttError(null);
    setKeptSavedIds((prev) =>
      (prev ?? savedAttachments.map((a) => a.id)).filter((x) => x !== id),
    );
  };

  const handleSaveAttachments = async () => {
    if (!task) return;
    setAttError(null);
    try {
      // 🔴 send the FULL merged list — link-multiple REPLACES the bound set.
      await editMut.mutateAsync({ id: task.id, body: { attachmentsIds: mergedIds } });
      // detail refetch (F3 invalidation) re-syncs keptSavedIds via the effect.
      await detailQ.refetch();
    } catch (e) {
      setAttError(e instanceof Error ? e.message : "Не вдалося зберегти вкладення");
    }
  };

  const isPending = reopenMut.isPending || postponeMut.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={onClose}
      aria-hidden
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full sm:w-[480px] overflow-y-auto bg-background shadow-2xl outline-none"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="text-base font-semibold">
            Деталі задачі
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити деталі"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {detailQ.isPending && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto w-5 h-5 animate-spin mb-2" /> Завантаження…
          </div>
        )}
        {detailQ.isError && (
          <div className="p-8 text-center">
            <AlertCircle className="mx-auto w-5 h-5 text-destructive mb-2" />
            <p className="text-sm text-destructive">
              {(detailQ.error as Error)?.message ?? "Не вдалося завантажити"}
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3 gap-1.5"
              onClick={() => void detailQ.refetch()}
            >
              <RotateCcw className="w-3.5 h-3.5" /> Ще раз
            </Button>
          </div>
        )}

        {task && (
          <>
            <div className="px-5 py-5 space-y-4">
              <h4 className="text-lg font-semibold leading-tight">{task.title}</h4>

              <dl className="text-sm space-y-2.5">
                <Row label="Статус">
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
                      isCompleted
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground",
                    )}
                  >
                    {isCompleted && <Check className="w-3 h-3" aria-hidden />}
                    {STATUS_LABEL[task.status] ?? task.status}
                  </span>
                </Row>
                <Row label="Дедлайн">
                  <span
                    className={cn(
                      task.overdue ? "text-destructive font-medium" : "",
                    )}
                  >
                    {task.deadlineLabel}
                  </span>
                </Row>
                <Row label="Пріоритет">
                  <span className="inline-flex items-center gap-1">
                    <Flag
                      className={cn(
                        "w-3.5 h-3.5",
                        task.priority === "high"
                          ? "text-destructive"
                          : task.priority === "medium"
                            ? "text-warning-foreground"
                            : "text-muted-foreground",
                      )}
                      aria-hidden
                    />
                    {PRIORITY_LABELS[task.priority]}
                    {task.priorityReason && (
                      <span className="text-xs text-muted-foreground">
                        · {task.priorityReason}
                      </span>
                    )}
                  </span>
                </Row>
                <Row label="Підтип">
                  <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                    {SUBTYPE_LABEL[task.subtype] ?? task.subtype}
                  </span>
                </Row>
                <Row label="Виконавець">{task.assigneeName ?? "—"}</Row>
                {/* D-142.W1 — спостерігачі: read+stream, керує лише edit-доступ (backend-owned gate) */}
                {(task.canManageWatchers || (task.watchers?.length ?? 0) > 0) && (
                  <Row label="Спостерігачі">
                    <WatcherPicker
                      value={task.watchers ?? []}
                      editable={!!task.canManageWatchers && !watchersMut.isPending}
                      busy={watchersMut.isPending}
                      excludeIds={task.assigneeId ? [task.assigneeId] : []}
                      onChange={(next: TaskWatcher[]) => {
                        setWatchersError(null);
                        watchersMut
                          .mutateAsync({ id: task.id, watchersIds: next.map((w) => w.id) })
                          .catch((e) =>
                            setWatchersError(
                              e instanceof Error ? e.message : "Не вдалося оновити спостерігачів",
                            ),
                          );
                      }}
                    />
                    {watchersError && (
                      <p role="alert" className="mt-1 text-xs text-destructive">{watchersError}</p>
                    )}
                  </Row>
                )}
                <Row label={task.accountId || task.holdingId || task.clientAccessLocked ? "Клієнт" : "Тип"}>
                  {task.clientAccessLocked ? (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Lock className="w-3.5 h-3.5" aria-hidden /> Немає доступу до клієнта
                    </span>
                  ) : task.accountId && task.accountName ? (
                    <Link
                      to={clientRoutes.accountAnchor(task.accountId)}
                      onClick={onClose}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <Building2 className="w-3.5 h-3.5" aria-hidden /> {task.accountName}
                    </Link>
                  ) : task.holdingId ? (
                    <Link
                      to={clientRoutes.client(task.holdingId)}
                      onClick={onClose}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      <Building2 className="w-3.5 h-3.5" aria-hidden /> {task.holdingName ?? "Картка клієнта"}
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Inbox className="w-3.5 h-3.5" aria-hidden /> Загальна задача
                    </span>
                  )}
                </Row>
                {isCompleted && task.outcome && (
                  <Row label="Результат">
                    <span className="inline-flex items-center rounded-md bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                      {OUTCOME_LABEL[task.outcome as TaskOutcome] ?? task.outcome}
                    </span>
                    {task.outcomeNote && (
                      <span className="ml-2 text-xs text-muted-foreground">{task.outcomeNote}</span>
                    )}
                  </Row>
                )}
                {isCompleted && task.legacy && (
                  <Row label="Якість даних">
                    <span className="inline-flex items-center rounded-md bg-destructive/10 px-1.5 py-0.5 text-[11px] font-semibold text-destructive">
                      ⚠ LEGACY · завершено без результату (до R1)
                    </span>
                  </Row>
                )}
                {task.source === "meeting" && task.sourceLabel && task.accountId && (
                  <Row label="Джерело">
                    <Link
                      to={clientRoutes.accountAnchor(task.accountId, "activities")}
                      onClick={onClose}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {task.sourceLabel}
                      <ExternalLink className="w-3 h-3" aria-hidden />
                    </Link>
                  </Row>
                )}
                {/* D-142.W1 (П-3) — watcher без доступу до source: locked-подача, не «дірка» */}
                {task.sourceAccessLocked && (
                  <Row label="Джерело">
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Lock className="w-3.5 h-3.5" aria-hidden /> Зустріч · немає доступу
                    </span>
                  </Row>
                )}
                {task.description && (
                  <Row label="Опис">
                    <span className="whitespace-pre-wrap">{task.description}</span>
                  </Row>
                )}
                {task.cCPostponeCount > 0 && (
                  <Row label="Переноси">
                    <span className="text-xs text-muted-foreground">
                      {task.cCPostponeCount}×
                    </span>
                  </Row>
                )}
              </dl>

              {/* ІСТОРІЯ — read-only journal synthesis (НЕ «Активність»-CRUD, R3) */}
              <div>
                <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  Історія
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold normal-case">read-only</span>
                </p>
                <div className="ml-1 space-y-2.5 border-l-2 pl-3.5">
                  <div>
                    <p className="text-[11px] text-muted-foreground">{shortDate(task.createdAt)}</p>
                    <p className="text-xs">
                      Задачу створено{task.createdByName ? ` (${task.createdByName})` : ""}
                      {task.source === "meeting" ? " · зі звіту зустрічі" : ""}
                    </p>
                  </div>
                  {task.cCPostponeCount > 0 && (
                    <div>
                      <p className="text-xs">Перенесено {task.cCPostponeCount}× · дедлайн {task.deadlineLabel}</p>
                    </div>
                  )}
                  {!isCompleted && task.overdue && (
                    <div>
                      <p className="text-xs text-destructive">Дедлайн минув → прострочена (derived, не статус)</p>
                    </div>
                  )}
                  {isCompleted && (
                    <div>
                      <p className="text-[11px] text-muted-foreground">{shortDate(task.modifiedAt)}</p>
                      <p className="text-xs">
                        Виконано
                        {task.outcome ? ` · ${OUTCOME_LABEL[task.outcome as TaskOutcome] ?? task.outcome}` : " · без результату (legacy)"}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* ВКЛАДЕННЯ — TASK-ATT v1.3 (W2). Editable add/remove until the
                  task is completed/cancelled; then view/download ONLY. */}
              <div>
                <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  Вкладення
                  {attachmentsLocked && (
                    <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold normal-case">
                      <Lock className="h-3 w-3" aria-hidden /> лише перегляд
                    </span>
                  )}
                </p>

                {attachmentsLocked ? (
                  // View/download only — no add, no remove.
                  <AttachmentList
                    parentType="Task"
                    parentId={task.id}
                    attachments={savedAttachments}
                  />
                ) : (
                  <>
                    <AttachmentUpload
                      parentType="Task"
                      parentId={task.id}
                      saved={keptSaved}
                      pending={pendingAtt}
                      onChange={(next) => {
                        setAttError(null);
                        setPendingAtt(next.pending);
                      }}
                      onRemoveSaved={handleRemoveSaved}
                      disabled={editMut.isPending}
                      label=""
                    />
                    {attachmentsDirty && (
                      <div className="mt-2 flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          className="h-9 gap-1.5"
                          onClick={() => void handleSaveAttachments()}
                          disabled={editMut.isPending}
                        >
                          {editMut.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Check className="h-3.5 w-3.5" />
                          )}
                          Зберегти вкладення
                        </Button>
                      </div>
                    )}
                    {attError && (
                      <p
                        role="alert"
                        className="mt-2 flex items-center gap-1.5 text-xs text-destructive"
                      >
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {attError}
                      </p>
                    )}
                  </>
                )}
              </div>

              {postponing && !isCompleted && (
                <div className="rounded-lg border bg-muted/40 p-3 space-y-2">
                  <p className="text-xs font-medium">Перенести на:</p>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9"
                      onClick={() => handlePostpone("tomorrow")}
                      disabled={isPending}
                    >
                      Завтра
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9"
                      onClick={() => handlePostpone("next_week")}
                      disabled={isPending}
                    >
                      Наступний тиждень
                    </Button>
                    <input
                      type="date"
                      value={customDate}
                      onChange={(e) => setCustomDate(e.target.value)}
                      className="rounded-md border px-2 py-1 text-xs"
                    />
                    <Button
                      size="sm"
                      className="h-9"
                      onClick={() => handlePostpone("custom")}
                      disabled={isPending || !customDate}
                    >
                      Підтвердити
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-9 gap-1"
                      onClick={() => setPostponing(false)}
                      disabled={isPending}
                    >
                      <ChevronLeft className="w-3.5 h-3.5" /> Назад
                    </Button>
                  </div>
                </div>
              )}

              {error && (
                <p
                  role="alert"
                  className="flex items-center gap-1.5 text-xs text-destructive"
                >
                  <AlertCircle className="w-3.5 h-3.5" /> {error}
                </p>
              )}
            </div>

            <div className="sticky bottom-0 border-t bg-background px-5 py-4">
              {!isOwn ? (
                <div
                  data-disabled-reason-code="not_owner"
                  role="note"
                  className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground"
                >
                  <Lock className="w-3.5 h-3.5" aria-hidden />
                  Лише виконавець може завершити чи перенести задачу
                </div>
              ) : isCompleted ? (
                <Button
                  type="button"
                  onClick={handleReopen}
                  disabled={isPending}
                  className="w-full h-11 gap-1.5"
                >
                  {reopenMut.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RotateCcw className="w-4 h-4" />
                  )}
                  Відновити
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setPostponing((v) => !v)}
                    disabled={isPending}
                    className="h-11 gap-1.5 flex-1"
                  >
                    <Calendar className="w-4 h-4" /> Перенести
                  </Button>
                  <Button
                    type="button"
                    onClick={() => setCompleteOpen(true)}
                    disabled={isPending}
                    className="h-11 gap-1.5 flex-1"
                  >
                    <Check className="w-4 h-4" />
                    Завершити →
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* canonical outcome-required completion (D-031) */}
      {task && (
        <TaskCompleteModal
          open={completeOpen}
          onClose={() => setCompleteOpen(false)}
          task={{ id: task.id, title: task.title, subtype: task.subtype, holdingId: task.holdingId }}
          onCompleted={onClose}
        />
      )}
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 items-baseline">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
